import React, { useMemo, useState, useEffect } from 'react';
import Header from '../components/Header';
import { useData } from '../context/DataContext';
import { useCacheCheck } from '../hooks/useCacheCheck';
import LeadershipSalesTable from '../components/LeadershipSalesTable';
import { CacheStatus } from '../components/CacheStatus';
import { ExportButton } from '../components/ExportButton';

export default function LeadershipTeamSalesUpdate() {
  const { data, loading, error, refreshData, loadingProgress } = useData();
  useCacheCheck();

  const currentYear = new Date().getFullYear();
  const [yearX, setYearX] = useState<number>(currentYear);
  const [yearY, setYearY] = useState<number>(currentYear - 1);
  const [availableArrivalYears, setAvailableArrivalYears] = useState<number[]>([]);

  // Extract available arrival years from data
  useEffect(() => {
    if (data && data.length > 0) {
      const arrivalYears = Array.from(new Set(data.map(row => row.arrival_year).filter(Boolean))).sort((a, b) => b - a);
      setAvailableArrivalYears(arrivalYears);
      
      // Set initial years based on available data
      if (arrivalYears.length > 0) {
        const mostRecentYear = arrivalYears[0]; // Since it's sorted descending
        if (arrivalYears.includes(currentYear)) {
          setYearX(currentYear);
          // Set yearY to the previous year if available, otherwise the most recent year
          const previousYear = currentYear - 1;
          if (arrivalYears.includes(previousYear)) {
            setYearY(previousYear);
          } else {
            setYearY(mostRecentYear);
          }
        } else {
          setYearX(mostRecentYear);
          // Set yearY to the second most recent year if available
          if (arrivalYears.length > 1) {
            setYearY(arrivalYears[1]);
          } else {
            setYearY(mostRecentYear);
          }
        }
      }
    }
  }, [data, currentYear]);

  // For Group Conversion calculation, we need all statuses to calculate total groups
  // The table will handle filtering for specific metrics as needed
  const filteredData = useMemo(() => data || [], [data]);

  // Debug logging to understand what's happening
  console.log('LeadershipTeamSalesUpdate render:', { 
    data: !!data, 
    dataLength: data?.length || 0,
    loading, 
    error, 
    loadingProgress,
    hasData: !!filteredData,
    filteredDataLength: filteredData?.length || 0
  });

  // Segment to macro section mapping (same as in LeadershipSalesTable)
  const segmentToMacroMapping: Record<string, string> = {
    'Ad-hoc Group': 'ADG',
    'Allotment contract': 'ADG',
    'ASIA - Ad-hoc Group': 'ASIA',
    'ASIA  - Ad-hoc Group': 'ASIA',
    'ASIA - Allotment Contract': 'ASIA',
    'ASIA - Group Series': 'ASIA',
    'ASIA - Rate Contract': 'ASIA',
    'Group Series': 'SERIES',
    'Rate Contract': 'ADG'
  };

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const metrics = [
    { key: 'confirmed', label: 'Confirmed Revenue' },
    { key: 'groups', label: 'Number of Stays' },
    { key: 'conversion', label: 'Group Conversion' },
    { key: 'materialization', label: 'Revenue Materialization' },
    { key: 'final', label: 'Final Revenue' },
  ];

  // Table data transformer for export
  const getTableDataForExport = () => {
    if (!data || data.length === 0) return [];

    // Aggregate data (same logic as LeadershipSalesTable)
    const aggregatedData: Record<string, any> = {};
    
    data.forEach(row => {
      const { segment, arrival_month, arrival_year, status, estimated_revenue, realised_revenue, request_id } = row;
      
      if (!segment || !arrival_month || !arrival_year || !request_id) return;
      
      const macroSection = segmentToMacroMapping[segment];
      if (!macroSection || macroSection === 'No Mapping') return;
      
      if (!aggregatedData[macroSection]) {
        aggregatedData[macroSection] = { months: {} };
      }
      
      if (!aggregatedData[macroSection].months[arrival_month]) {
        aggregatedData[macroSection].months[arrival_month] = {};
      }
      
      if (!aggregatedData[macroSection].months[arrival_month][arrival_year]) {
        aggregatedData[macroSection].months[arrival_month][arrival_year] = {
          confirmed: 0,
          groups: 0,
          conversion: { total: new Set(), booked: new Set() },
          materialization: { final: 0 },
          final: 0
        };
      }
      
      const monthData = aggregatedData[macroSection].months[arrival_month][arrival_year];
      monthData.conversion.total.add(request_id);
      
      if (status === 'Booked') {
        monthData.confirmed += estimated_revenue || 0;
        monthData.groups += 1;
        monthData.conversion.booked.add(request_id);
        monthData.materialization.final += realised_revenue || 0;
        monthData.final += realised_revenue || 0;
      }
    });

    // Helper to get value
    const getValue = (macroSection: string, metric: string, month: number, year: number): any => {
      const monthData = aggregatedData[macroSection]?.months[month]?.[year];
      if (!monthData) return metric === 'conversion' || metric === 'materialization' ? 0 : 0;
      
      switch (metric) {
        case 'confirmed':
          return monthData.confirmed;
        case 'groups':
          return monthData.groups;
        case 'conversion':
          return monthData.conversion.total.size > 0 
            ? (monthData.conversion.booked.size / monthData.conversion.total.size) * 100 
            : 0;
        case 'materialization':
          return monthData.confirmed > 0 
            ? (monthData.materialization.final / monthData.confirmed) * 100 
            : 0;
        case 'final':
          return monthData.final;
        default:
          return 0;
      }
    };

    const getTotalValue = (metric: string, month: number, year: number): number => {
      let confirmed = 0;
      let groups = 0;
      let conversionTotal = 0;
      let conversionBooked = 0;
      let materializationFinal = 0;
      let finalRevenue = 0;

      for (const macroSection of Object.keys(aggregatedData)) {
        const monthData = aggregatedData[macroSection]?.months[month]?.[year];
        if (!monthData) continue;
        confirmed += monthData.confirmed;
        groups += monthData.groups;
        conversionTotal += monthData.conversion.total.size;
        conversionBooked += monthData.conversion.booked.size;
        materializationFinal += monthData.materialization.final;
        finalRevenue += monthData.final;
      }

      switch (metric) {
        case 'confirmed':
          return confirmed;
        case 'groups':
          return groups;
        case 'conversion':
          return conversionTotal > 0 ? (conversionBooked / conversionTotal) * 100 : 0;
        case 'materialization':
          return confirmed > 0 ? (materializationFinal / confirmed) * 100 : 0;
        case 'final':
          return finalRevenue;
        default:
          return 0;
      }
    };

    const getAnnualValue = (macroSection: string, metric: string, year: number): number => {
      let confirmed = 0;
      let groups = 0;
      let conversionTotal = 0;
      let conversionBooked = 0;
      let materializationFinal = 0;
      let finalRevenue = 0;

      for (let month = 1; month <= 12; month++) {
        const monthData = aggregatedData[macroSection]?.months[month]?.[year];
        if (!monthData) continue;
        confirmed += monthData.confirmed;
        groups += monthData.groups;
        conversionTotal += monthData.conversion.total.size;
        conversionBooked += monthData.conversion.booked.size;
        materializationFinal += monthData.materialization.final;
        finalRevenue += monthData.final;
      }

      switch (metric) {
        case 'confirmed':
          return confirmed;
        case 'groups':
          return groups;
        case 'conversion':
          return conversionTotal > 0 ? (conversionBooked / conversionTotal) * 100 : 0;
        case 'materialization':
          return confirmed > 0 ? (materializationFinal / confirmed) * 100 : 0;
        case 'final':
          return finalRevenue;
        default:
          return 0;
      }
    };

    const getAnnualTotalValue = (metric: string, year: number): number => {
      let confirmed = 0;
      let groups = 0;
      let conversionTotal = 0;
      let conversionBooked = 0;
      let materializationFinal = 0;
      let finalRevenue = 0;

      for (let month = 1; month <= 12; month++) {
        for (const macroSection of Object.keys(aggregatedData)) {
          const monthData = aggregatedData[macroSection]?.months[month]?.[year];
          if (!monthData) continue;
          confirmed += monthData.confirmed;
          groups += monthData.groups;
          conversionTotal += monthData.conversion.total.size;
          conversionBooked += monthData.conversion.booked.size;
          materializationFinal += monthData.materialization.final;
          finalRevenue += monthData.final;
        }
      }

      switch (metric) {
        case 'confirmed':
          return confirmed;
        case 'groups':
          return groups;
        case 'conversion':
          return conversionTotal > 0 ? (conversionBooked / conversionTotal) * 100 : 0;
        case 'materialization':
          return confirmed > 0 ? (materializationFinal / confirmed) * 100 : 0;
        case 'final':
          return finalRevenue;
        default:
          return 0;
      }
    };

    const exportRows: any[] = [];

    const appendExportRows = (
      macroSection: string,
      valueGetter: (metric: string, month: number, year: number) => number,
      annualValueGetter: (metric: string, year: number) => number,
    ) => {
      metrics.forEach(metric => {
        const row: any = {
          'Macro Segment': macroSection,
          'KPI': metric.label
        };

        months.forEach((monthName, monthIndex) => {
          const monthNum = monthIndex + 1;
          const valueX = valueGetter(metric.key, monthNum, yearX);
          const valueY = valueGetter(metric.key, monthNum, yearY);

          if (metric.key === 'conversion' || metric.key === 'materialization') {
            row[`${monthName} ${yearX}`] = valueX > 0 ? valueX : 0;
            row[`${monthName} ${yearY}`] = valueY > 0 ? valueY : 0;
          } else {
            row[`${monthName} ${yearX}`] = valueX || 0;
            row[`${monthName} ${yearY}`] = valueY || 0;
          }
        });

        const annualX = annualValueGetter(metric.key, yearX);
        const annualY = annualValueGetter(metric.key, yearY);
        if (metric.key === 'conversion' || metric.key === 'materialization') {
          row[`Total ${yearX}`] = annualX > 0 ? annualX : 0;
          row[`Total ${yearY}`] = annualY > 0 ? annualY : 0;
        } else {
          row[`Total ${yearX}`] = annualX || 0;
          row[`Total ${yearY}`] = annualY || 0;
        }

        exportRows.push(row);
      });
    };

    const macroSections = ['ADG', 'SERIES', 'ASIA'];

    macroSections.forEach(macroSection => {
      if (!aggregatedData[macroSection]) return;
      appendExportRows(
        macroSection,
        (metric, month, year) => getValue(macroSection, metric, month, year),
        (metric, year) => getAnnualValue(macroSection, metric, year),
      );
    });

    appendExportRows('Total', getTotalValue, getAnnualTotalValue);

    return exportRows;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="max-w-[90rem] mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="page-title">Leadership Team Sales Update</h1>
          <div className="flex items-center space-x-4">
            <ExportButton
              reportName="Leadership Team Sales Update"
              data={data || []}
              filters={{
                selectedCreationYears: new Set(),
                selectedArrivalYears: new Set(),
                selectedManagers: new Set(),
                selectedSegments: new Set(),
                selectedClients: new Set(),
                selectedHotels: new Set(),
                selectedHotelGroups: new Set(),
                selectedArrivalMonths: new Set(),
                selectedArrivalQuarters: new Set(),
                selectedCreationMonths: new Set(),
                selectedCreationQuarters: new Set(),
                allClientsSelected: true,
                allHotelsSelected: true,
                allHotelGroupsSelected: true
              }}
              tableDataTransformer={getTableDataForExport}
            />
            <CacheStatus refreshData={refreshData} />
          </div>
        </div>
        <div className="flex gap-4 mb-4 justify-end items-center">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-700">Year X</label>
            <select value={yearX} onChange={e => setYearX(Number(e.target.value))} className="border rounded px-2 py-1">
              {availableArrivalYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-700">Year Y</label>
            <select value={yearY} onChange={e => setYearY(Number(e.target.value))} className="border rounded px-2 py-1">
              {availableArrivalYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
        {loadingProgress && (
          <div className="mb-4 bg-blue-50 text-blue-700 p-2 rounded-md text-center">
            {loadingProgress}
          </div>
        )}
        <div className="bg-white rounded-lg shadow">
          {loading && !data ? (
            <div className="p-8 text-center">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-blue-600 transition ease-in-out duration-150">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading report data...
              </div>
            </div>
          ) : error ? (
            <div className="p-4 text-red-500 text-center">
              <div className="text-lg font-semibold mb-2">Error loading data</div>
              <div className="text-sm">{error}</div>
            </div>
          ) : !data || data.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-sm">Please wait for data to load</div>
            </div>
          ) : (
            <LeadershipSalesTable data={filteredData} yearX={yearX} yearY={yearY} />
          )}
        </div>
      </div>
    </div>
  );
}
